import { loadStripe } from "@stripe/stripe-js";

/**
 * Mints a Stripe Radar session id for card fraud detection at signup (DAT-1731).
 *
 * The session is created client-side via Stripe.js before the SetupIntent is
 * created so Radar can attach device/browser fraud signals to it. It is sent to
 * the backend's create-setup-intent call as `radar_session`.
 *
 * This is entirely best-effort: on any failure (Stripe.js unavailable, Radar not
 * enabled, missing key, network error) it resolves to `undefined` so the
 * card-add flow proceeds unchanged. The backend treats the session as optional.
 *
 * @param publishableKey Stripe publishable key for the account the SetupIntent
 * will be created on. Must match that account or Radar ignores the session.
 */
export async function createRadarSessionId(
  publishableKey: string | undefined
): Promise<string | undefined> {
  if (!publishableKey) return undefined;

  try {
    const stripe = await loadStripe(publishableKey);
    if (!stripe) return undefined;

    const { radarSession, error } = await stripe.createRadarSession();
    if (error || !radarSession) return undefined;

    const sessionId = (radarSession as { id?: unknown }).id;
    return typeof sessionId === "string" ? sessionId : undefined;
  } catch {
    // Never let fraud-signal collection break card setup.
    return undefined;
  }
}
